import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Users, Search, Briefcase, GraduationCap, Eye, KeyRound, Pencil, Loader2,
  UserCheck, UserMinus, Trash2,
} from 'lucide-react';
import { api, ROLE_LABELS } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import StaffDetailDialog from '@/components/staff/StaffDetailDialog';
import StudentAccountInfoDialog from '@/components/students/StudentAccountInfoDialog';
import { confirmDialog } from '@/components/ui/confirm-dialog';
import UnduhDataGtkButton from '@/components/staff/UnduhDataGtkButton';
import ExportTemplateButton from '@/components/students/ExportTemplateButton';
import { KOLOM_DATA_GTK } from '@/lib/dataGtkKolom';
import { PERAN_GURU_GTK, PERAN_TENDIK_GTK, jenisGtk } from '@/lib/jenisGtk';
import KelengkapanGtkCell from '@/components/staff/KelengkapanGtkCell';
import ImportDataMasterButton from '@/components/students/ImportDataMasterButton';

const GURU_ROLES = PERAN_GURU_GTK;
const TENDIK_ROLES = PERAN_TENDIK_GTK;
const STATUS_KEPEGAWAIAN_LIST = ['PNS', 'PPPK', 'Non ASN'];
const BELUM_DIISI = 'Belum diisi';

/**
 * Label status kepegawaian dari data tersimpan. Form detail GTK & impor menyimpan kode
 * (pns / pppk / non_asn) di `status_kepegawaian`; data lama memakai `employee_status`
 * (mis. "PNS"). Keduanya dinormalkan ke label PNS / PPPK / Non ASN.
 */
export function labelKepegawaian(u) {
  const raw = String(u?.status_kepegawaian || u?.employee_status || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!raw) return null;
  if (raw === 'pns') return 'PNS';
  if (raw === 'pppk' || raw === 'p3k') return 'PPPK';
  if (['non_asn', 'nonasn', 'honorer', 'gtt', 'ptt', 'gty', 'pty'].includes(raw)) return 'Non ASN';
  return null;
}
// Roles that see the same all-GTK admin-style view/filters, but without any
// account/edit/delete actions (view-only) — only the Detail button remains.
const VIEW_ONLY_BROAD_ROLES = ['kepala_sekolah'];

export default function AdminGTKPage() {
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  const canManage = !VIEW_ONLY_BROAD_ROLES.includes(activeRole);
  const [tab, setTab] = useState('guru');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [detailUser, setDetailUser] = useState(null);
  const [accountUser, setAccountUser] = useState(null);
  const [jabatanList, setJabatanList] = useState([]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [usersRes, jabatanRes, kelengkapanRes] = await Promise.all([
        api.get('/users', { params: { exclude_mutation: true } }),
        api.get('/jabatan/active'),
        // % kelengkapan dari server; bila belum tersedia, dihitung di browser (lib/kelengkapanGtk).
        api.get('/gtk/kelengkapan').catch(() => null),
      ]);
      const kelMap = Object.fromEntries((kelengkapanRes?.data?.items || []).map((it) => [it.id, it.kelengkapan]));
      setUsers((usersRes.data || []).map((u) => (kelMap[u.id] ? { ...u, kelengkapan: kelMap[u.id] } : u)));
      setJabatanList(jabatanRes.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = users.filter((u) => {
    const roles = u.roles || [];
    const isGuru = jenisGtk(roles) === 'guru';
    const isTendik = jenisGtk(roles) === 'tendik';
    if (tab === 'guru' && !isGuru) return false;
    if (tab === 'tendik' && !isTendik) return false;
    if (roleFilter !== 'all' && !roles.includes(roleFilter)) return false;
    if (search) {
      const s = search.toLowerCase();
      return (u.full_name || '').toLowerCase().includes(s) ||
             (u.peg_id || '').toLowerCase().includes(s) ||
             (u.nip_nuptk || '').includes(search) ||
             (u.username || '').toLowerCase().includes(s);
    }
    return true;
  });

  const counts = {
    guru: users.filter((u) => jenisGtk(u.roles) === 'guru').length,
    tendik: users.filter((u) => jenisGtk(u.roles) === 'tendik').length,
  };

  const guruUsers = users.filter((u) => jenisGtk(u.roles) === 'guru');
  const tendikUsers = users.filter((u) => jenisGtk(u.roles) === 'tendik');

  const genderStats = (list) => ({
    L: list.filter((u) => u.gender === 'L').length,
    P: list.filter((u) => u.gender === 'P').length,
  });
  const kepegawaianStats = (list) => [...STATUS_KEPEGAWAIAN_LIST, BELUM_DIISI].reduce((acc, s) => {
    acc[s] = list.filter((u) => (labelKepegawaian(u) || BELUM_DIISI) === s).length;
    return acc;
  }, {});

  const guruGender = genderStats(guruUsers);
  const tendikGender = genderStats(tendikUsers);
  const guruKepegawaian = kepegawaianStats(guruUsers);
  const tendikKepegawaian = kepegawaianStats(tendikUsers);

  const handleDelete = async (u) => {
    if (!(await confirmDialog(`Hapus data GTK ${u.full_name} (${u.username})?`))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('Data GTK berhasil dihapus');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus data GTK');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-gtk-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Badge className="bg-[#006837]/10 text-[#006837] border-[#006837]/20 mb-2">
            <Briefcase className="h-3 w-3 mr-1" /> Data GTK
          </Badge>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Data Guru & Tenaga Kependidikan</h1>
          <p className="text-sm text-slate-600 mt-1">Biodata lengkap GTK madrasah</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2" data-testid="aksi-data-gtk">
            <ExportTemplateButton
              endpoint="/gtk/import-template"
              kolom={KOLOM_DATA_GTK}
              sheet="Data GTK"
              filename="Template_Data_GTK.xlsx"
              judul="Template Impor Data GTK"
              pilihBagian
              testid="btn-export-template-gtk"
            />
            <ImportDataMasterButton
              onSelesai={(h) => { if (!h.tiruan && h.ringkasan.berhasil > 0) refresh(); }}
              endpoint="/gtk/import-kelengkapan"
              kolom={KOLOM_DATA_GTK}
              sheet="Data GTK"
              judul="Import Pelengkapan Data GTK"
              namaData="GTK"
              testid="btn-import-gtk"
            />
            <UnduhDataGtkButton jumlah={counts} users={users} disabled={loading} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => setTab('guru')}
          className={`rounded-xl border p-4 text-left transition-all ${tab === 'guru' ? 'border-[#006837] bg-[#006837]/5 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
          data-testid="card-guru">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Guru</span>
            <GraduationCap className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="text-3xl font-extrabold tabular-nums text-slate-900">{counts.guru}</div>
          <div className="text-xs text-slate-500 mt-0.5">guru mata pelajaran / khusus</div>
        </button>
        <button onClick={() => setTab('tendik')}
          className={`rounded-xl border p-4 text-left transition-all ${tab === 'tendik' ? 'border-[#006837] bg-[#006837]/5 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
          data-testid="card-tendik">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Tenaga Kependidikan</span>
            <Briefcase className="h-4 w-4 text-purple-600" />
          </div>
          <div className="text-3xl font-extrabold tabular-nums text-slate-900">{counts.tendik}</div>
          <div className="text-xs text-slate-500 mt-0.5">staf TU, perpustakaan, dll</div>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <GraduationCap className="h-4 w-4 text-emerald-600" /> Guru
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-blue-700">{guruGender.L}</div>
                <div className="text-xs text-blue-600">Laki-laki</div>
              </div>
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-rose-700">{guruGender.P}</div>
                <div className="text-xs text-rose-600">Perempuan</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
              {STATUS_KEPEGAWAIAN_LIST.map((s) => (
                <Badge key={s} variant="outline" className="text-xs" data-testid={`stat-guru-${s}`}>{s}: {guruKepegawaian[s]}</Badge>
              ))}
              {guruKepegawaian[BELUM_DIISI] > 0 && (
                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">{BELUM_DIISI}: {guruKepegawaian[BELUM_DIISI]}</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Briefcase className="h-4 w-4 text-purple-600" /> Tenaga Kependidikan
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-blue-700">{tendikGender.L}</div>
                <div className="text-xs text-blue-600">Laki-laki</div>
              </div>
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-rose-700">{tendikGender.P}</div>
                <div className="text-xs text-rose-600">Perempuan</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
              {STATUS_KEPEGAWAIAN_LIST.map((s) => (
                <Badge key={s} variant="outline" className="text-xs" data-testid={`stat-tendik-${s}`}>{s}: {tendikKepegawaian[s]}</Badge>
              ))}
              {tendikKepegawaian[BELUM_DIISI] > 0 && (
                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">{BELUM_DIISI}: {tendikKepegawaian[BELUM_DIISI]}</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200" data-testid="gtk-tabs">
          <TabsTrigger value="guru" data-testid="tab-guru">Guru ({counts.guru})</TabsTrigger>
          <TabsTrigger value="tendik" data-testid="tab-tendik">Tenaga Kependidikan ({counts.tendik})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-4">
          <Card>
            <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Hidden dummy fields to prevent browser autofill on search field */}
              <input type="text" name="fake-username" autoComplete="username" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
              <input type="password" name="fake-password" autoComplete="current-password" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Cari nama, Peg ID, NIP/NUPTK..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-gtk"
                  autoComplete="off"
                  name="gtk-search-query"
                  type="search"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="filter-role"><SelectValue placeholder="Filter Peran" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Peran</SelectItem>
                  {(tab === 'guru' ? GURU_ROLES : TENDIK_ROLES).map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-[#006837]" />
                  Memuat data GTK...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="gtk-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12 text-center">NO</TableHead>
                        <TableHead>NAMA</TableHead>
                        <TableHead>PEG ID</TableHead>
                        <TableHead>L/P</TableHead>
                        <TableHead>PERAN</TableHead>
                        <TableHead>JABATAN</TableHead>
                        <TableHead>STATUS KEPEGAWAIAN</TableHead>
                        <TableHead className="min-w-[130px]">KELENGKAPAN</TableHead>
                        <TableHead>STATUS</TableHead>
                        <TableHead className="text-right">AKSI</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((u, i) => {
                        const userJabatanNames = (u.jabatan_ids || []).map(jid => {
                          const j = jabatanList.find(jab => jab.id === jid);
                          return j?.name;
                        }).filter(Boolean);

                        return (
                        <TableRow key={u.id} data-testid={`gtk-row-${u.id}`}>
                          <TableCell className="text-center text-slate-500 font-mono">{i + 1}</TableCell>
                          <TableCell className="font-semibold">{u.full_name}</TableCell>
                          <TableCell className="font-mono text-xs">{u.peg_id || '-'}</TableCell>
                          <TableCell>
                            {u.gender === 'L' ? <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">L</Badge> :
                             u.gender === 'P' ? <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">P</Badge> :
                             <span className="text-slate-400 text-xs">-</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(u.roles || []).filter((r) => r !== 'siswa').slice(0, 3).map((r) => (
                                <Badge key={r} variant="outline" className="text-xs">{ROLE_LABELS[r] || r}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {userJabatanNames.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {userJabatanNames.map((jName, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">{jName}</Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="italic text-slate-400 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {labelKepegawaian(u) ? (
                              <Badge variant="outline" className="text-xs">{labelKepegawaian(u)}</Badge>
                            ) : (
                              <span className="italic text-slate-400 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell data-testid={`gtk-kelengkapan-${u.id}`}>
                            <KelengkapanGtkCell user={u} linkDetail={`/admin/gtk/${u.id}`} />
                          </TableCell>
                          <TableCell>
                            {u.mutation_type === 'keluar' ? <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">Mutasi Keluar</Badge> :
                             u.mutation_type === 'masuk' ? <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">Mutasi Masuk</Badge> :
                             u.is_active !== false ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Aktif</Badge> :
                             <Badge variant="outline" className="text-xs">Nonaktif</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" onClick={() => navigate(`/admin/gtk/${u.id}`)}
                                className="gap-1 border-[#006837]/40 text-[#006837] hover:bg-[#006837]/5"
                                data-testid={`gtk-detail-${u.id}`}>
                                <Eye className="h-3.5 w-3.5" /> Detail
                              </Button>
                              {canManage && (
                                <Button size="sm" variant="outline" onClick={() => setAccountUser(u)}
                                  className="gap-1" data-testid={`gtk-account-${u.id}`}>
                                  <KeyRound className="h-3.5 w-3.5" /> Info Akun
                                </Button>
                              )}
                              {canManage && (
                                <Button size="sm" variant="ghost" onClick={() => setDetailUser({ ...u, _autoEdit: true })}
                                  className="gap-1 text-amber-700 hover:bg-amber-50" data-testid={`gtk-edit-${u.id}`}>
                                  <Pencil className="h-3.5 w-3.5" /> Edit
                                </Button>
                              )}
                              {canManage && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(u)}
                                  className="gap-1 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                                  data-testid={`gtk-delete-${u.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Hapus
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                      {filtered.length === 0 && (
                        <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-500">
                          <Briefcase className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                          <div className="font-semibold">Tidak ada data GTK</div>
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {detailUser && (
        <StaffDetailDialog
          user={detailUser}
          autoEdit={detailUser._autoEdit}
          open={!!detailUser}
          onClose={() => { setDetailUser(null); refresh(); }}
        />
      )}
      {accountUser && (
        <StudentAccountInfoDialog
          student={accountUser}
          open={!!accountUser}
          onClose={() => {
            setAccountUser(null);
            // Delay refresh to prevent autofill issues
            setTimeout(() => refresh(), 100);
          }}
          isGTK={true}
        />
      )}
    </div>
  );
}
